import { createContext, useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';
import { getMe, logout as logoutRequest } from '@/lib/api-client/auth';
import type { AuthUser, ManageableGuild } from '@/lib/api-client/auth';
import { ApiError } from '@/lib/api-client/client';
import { env } from '@/lib/env';

export type AuthStatus = 'loading' | 'authenticated' | 'unauthenticated';

interface AuthContextValue {
  status: AuthStatus;
  user: AuthUser | null;
  guilds: ManageableGuild[];
  /** Full-page navigation to the Worker's real OAuth2 login route — not a React Router link, since this leaves the SPA entirely. */
  login: () => void;
  logout: () => Promise<void>;
  /** Re-fetches /me — e.g. after returning from the OAuth callback redirect. */
  refresh: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * docs/ARCHITECTURE.md §1.7. Populated once on app load via a real call to
 * GET /api/auth/me — never a hardcoded/fake "logged in" state. If that call
 * fails with UNAUTHENTICATED, status becomes 'unauthenticated'; any other
 * failure (network, 500, etc.) also resolves to 'unauthenticated' rather
 * than hanging on 'loading' forever, since from the UI's perspective both
 * cases mean "can't currently prove who you are."
 */
export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<AuthStatus>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [guilds, setGuilds] = useState<ManageableGuild[]>([]);

  const refresh = useCallback(async () => {
    try {
      const me = await getMe();
      setUser(me.user);
      setGuilds(me.guilds);
      setStatus('authenticated');
    } catch (err) {
      setUser(null);
      setGuilds([]);
      setStatus('unauthenticated');
      if (!(err instanceof ApiError && err.code === 'UNAUTHENTICATED')) {
        // Genuinely unexpected (network/server error) — worth knowing about
        // in dev; still resolves to 'unauthenticated' either way, since the
        // UI has exactly two states to render regardless of which occurred.
        console.error('Auth check failed', err);
      }
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const login = useCallback(() => {
    window.location.href = `${env.apiBaseUrl}/api/auth/login`;
  }, []);

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
    setGuilds([]);
    setStatus('unauthenticated');
  }, []);

  return (
    <AuthContext.Provider value={{ status, user, guilds, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}
